import TableView from "@client/content/controls/TableView/container";
import { getRootContainerProps } from "@client/content/lib/helper";
import { type RootContainerProps } from "@client/content/types";
import Circle from "@client/controls/Circle/component";
import Select from "@client/controls/Inputs/Select/component";
import { useDidUpdate } from "@client/lib/hooks";
import { type OptionItem, ScreenType } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import * as React from "react";
import AddEditModal from "../AddEditModal/container";
import { Approved, Forum, PostAvatar } from "../Components";
import { type Forums } from "../types";

export type MapProps = RootContainerProps & {
  canAdd: boolean;
  fieldStyle: React.CSSProperties;
  forumId: string | null;
  forumOptions: Forums.Api.ForumOptionItem[];

  isEditMode: boolean;
  items: Forums.Post[];
  page: number;
  screen: ScreenType;
  searchText: string | null;
  size: number;
  status: string;
  userId?: number;
};

export interface ActionProps {
  load: (page: number, forumId: string | null) => void;

  onAddClick: () => void;
  onDeleteClick: (item: Forums.Post) => Promise<any>;
  onItemClick: (item: Forums.Post) => void;
  onPageChanged: (page: number, size: number) => void;
  onSearch: (searchText: string) => void;
}

export default (props: ActionProps & MapProps) => {
  const i18n = useI18n();

  useDidUpdate(() => {
    props.load(1, null);
  }, [props.forumOptions]);

  const forumOptions = React.useMemo<OptionItem<string | null>[]>(
    () => [
      { label: i18n.allForums, value: null },
      ...props.forumOptions.map((x) => ({ ...x, image: <Circle color={x.color} size={8} /> }))
    ],
    [props.forumOptions]
  );

  return (
    <>
      <TableView
        {...getRootContainerProps(props)}
        className="posts_table"
        idKey="ticketId"
        fixed
        isLoading={props.status === "LoadingPosts"}
        columns={[
          {
            key: "subject",
            name: i18n.topic,
            dataType: "string",
            data: (x: Forums.Post) => (
              <>
                <div className="single-line-ellipsis">{x.subject}</div>
                <div className="title_info">
                  <Forum color={x.forumColor} name={x.forumName} />
                  {x.contactUserId === props.userId ? <Approved isApproved={x.isApproved} /> : null}
                </div>
              </>
            )
          },
          {
            key: "contactUserId",
            name: i18n.author,
            dataType: "string",
            data: (x: Forums.Post) => <PostAvatar noName={props.screen === ScreenType.Mobile} post={x} />
          },
          {
            key: "approvedTicketCommentCount",
            name: i18n.replies,
            dataType: "number"
          }
        ]}
        add={{
          label: i18n.createPost,
          onClick: props.onAddClick,
          isDisabled: !props.canAdd
        }}
        data={props.items}
        filterFn={props.onSearch}
        onPageChanged={props.onPageChanged}
        onRowClick={props.isEditMode ? undefined : props.onItemClick}
        pageNumber={props.page}
        pageSize={props.size}
        searchText={props.searchText}
        noDataText={i18n.infoNoPosts}
        toolbar={{
          showAdd: !!props.userId,
          showSearch: true,
          leftButtons: [
            <Select
              isRequired
              options={forumOptions}
              style={props.fieldStyle}
              value={props.forumId}
              onChange={(x) => props.load(1, x)}
            />
          ]
        }}
      />

      <AddEditModal {...getRootContainerProps(props)} />
    </>
  );
};
